<style scoped>
legend {
    margin-bottom: 0.5rem;
}
.editor-page-type {
    margin-bottom: 10px;
}
.modal-content {
    width: min(90vw, 960px);
}
</style>
<template>
    <div class="modal-content" ref="container">
        <form>
            <div class="modal-header clearfix">
                <h3 class="modal-title pull-left">Link for {{ $props.name }} <strong v-if="$props.testMode" class="badge badge-warning ml-1">Test Data</strong></h3>
                <button type="button" aria-label="Close" class="close icon icon-close" @click.prevent="close()" />
            </div>
            <div class="modal-body">
                <div class="clearfix">
                    <fieldset role="radiogroup" class="mb-2" aria-required="true">
                        <legend class="editor-label">Type</legend>
                        <div class="row editor-page-types d-flex flex-row form-row">
                            <!-- {{ type }} -->
                            <div v-for="typeOption in $props.typeOptions" @click.prevent="type = typeOption.value" class="col-lg-4 col-md-6">
                                <!-- {{ type==typeOption.value }} @ {{ typeOptions.value }} -->
                                <label class="editor-page-type card card-body h-100" :class="type == typeOption.value ? ['selected'] : []">
                                    <input :readonly="!canEdit" type="radio" name="type" v-model="type" :value="typeOption.value" :id="typeOption.value" @click.prevent="setType(typeOption.value)" />
                                    <h3 class="card-title">{{ typeOption.text }}</h3>
                                    <span class="editor-help-text" v-if="typeOption.description">{{ typeOption.description }}</span>
                                </label>
                            </div>
                        </div>
                    </fieldset>
                    <div v-show="type != null">
                        <div v-if="linkHelp" v-html="linkHelp" class="pl-2"></div>
                        <div class="form-group" v-if="type == 'Gov2GoServiceEvent'">
                            <label for="linkUrl" class="control-label">Service ID</label>
                            <input :readonly="!canEdit" type="text" class="form-control" name="linkUrl" id="linkUrl" v-model="linkUrl" />
                        </div>
                        <template v-else>
                            <div class="row">
                                <div class="form-group col-2">
                                    <label class="control-label" for="linkHttpMethod">HTTP Method</label>
                                    <select id="linkHttpMethod" v-model="linkHttpMethod" class="form-control">
                                        <option>GET</option>
                                        <option>POST</option>
                                    </select>
                                </div>
                                <div class="form-group col-10">
                                    <label for="linkUrl" class="control-label">Link URL</label>
                                    <input :readonly="!canEdit" type="text" class="form-control" name="linkUrl" id="linkUrl" v-model="linkUrl" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label class="control-label" for="authType">Authorization Types</label>
                                <select id="authType" v-model="authType" class="form-control" :disabled="!canEdit">
                                    <option v-for="option in filteredAuthTypes" :value="option.value" :key="option.value">{{ option.label }}</option>
                                </select>
                            </div>
                            <div class="form-group" v-if="authType == basicAuthentication.toString() || authType == customHeader.toString()">
                                <label for="linkUsername" class="control-label">{{ usernameLabel }}</label>
                                <input :readonly="!canEdit" type="text" class="form-control" name="linkUsername" id="linkUsername" v-model="linkUsername" />
                            </div>
                            <div class="form-group" v-if="authType != null && authType != noAuthentication.toString()">
                                <label for="linkPassword" class="control-label">{{ passwordLabel }}</label>
                                <input :readonly="!canEdit" type="password" class="form-control" name="linkPassword" id="linkPassword" v-model="linkPassword" />
                            </div>
                        </template>
                        <div class="form-group">
                            <label for="cacheExpiresInSeconds" class="control-label">Cache Lifespan (in seconds)</label>
                            <input :readonly="!canEdit" type="text" class="form-control" name="cacheExpiresInSeconds" id="cacheExpiresInSeconds" v-model="cacheExpiresInSeconds" />
                        </div>
                        <div class="form-group" v-if="showIgnoreCacheControlHeader">
                            <label for="ignoreCacheControlHeader" class="control-label">Ignore Cache-Control header in API response</label>
                            <span class="switch" v-on:click="toggleIgnoreCacheControlHeader">
                                <input type="checkbox" :disabled="!canEdit" name="ignoreCacheControlHeader" id="ignoreCacheControlHeader" v-model="ignoreCacheControlHeader" />
                                <span class="slider"></span>
                            </span>
                        </div>
                        <div class="form-group" v-if="showSupportWriting">
                            <label for="supportWriting" class="control-label">API Writable</label>
                            <span class="switch" v-on:click="toggleSupportWriting">
                                <input type="checkbox" :disabled="!canEdit" name="supportWriting" id="supportWriting" v-model="supportWriting" />
                                <span class="slider"></span>
                            </span>
                        </div>
                    </div>
                    <span v-show="preview?.data != null" class="data-source-preview"> Showing first {{ preview?.data == null ? 0 : preview.data.data.length }} rows. Full file has {{ preview?.count }} rows. </span>
                </div>
                <div class="table-responsive" v-if="preview != null && preview?.data != null" style="max-height: 70vh">
                    <table class="table table-bordered" style="background: white; margin-bottom: 0">
                        <thead>
                            <tr style="background: #f8f8f8">
                                <th v-for="key in preview?.data.keys ?? []">
                                    {{ key }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in preview.data.data">
                                <td v-for="column in row">
                                    {{ column }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="modal-footer">
                <span class="text-muted mr-3" v-for="error in confirm?.errors ?? []" id="errors">{{ error }}</span>
                <button :disabled="!canEdit" type="button" class="btn btn-primary" :disable="confirm?.status == 'in-progress'" @click="confirmExternalLink" id="linkBtn">
                    Link
                    <span v-show="confirm?.status == 'fail'" style="color: red" class="icon icon-attention"></span>
                    <LoadingWheel v-if="confirm?.status == 'in-progress'" style="position: relative; top: -2px; display: inline-block" :width="16"></LoadingWheel>
                </button>
                <span class="text-muted mr-3" v-for="error in preview?.errors ?? []">{{ error }}</span>
                <button :disabled="!canEdit" type="button" class="btn btn-outline-secondary" :disable="preview?.status == 'in-progress'" @click="previewExternalLink" id="previewBtn">
                    Preview
                    <span v-show="preview?.status == 'fail'" style="color: red" class="icon icon-attention"></span>
                    <LoadingWheel v-if="preview?.status == 'in-progress'" style="position: relative; top: -2px; display: inline-block" :width="16"></LoadingWheel>
                </button>
                <button type="button" class="btn btn-bare" @click.prevent="close()">Cancel</button>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, SetupContext, watch } from "vue";
import { useMainStore } from "@/mainStore";
import { asyncComputed } from "@vueuse/core";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { AuthType } from "@cs/AuthType";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";

//import HelpIcon from "@/lib/HelpIcon.3.vue";
type Props = {
    name: string;
    testMode: boolean;
    confirmCallback: (data: Record<string, unknown>) => Promise<void>;
    initialLinkUrl: string;
    initialLinkHttpMethod: null | "GET" | "POST";
    initialLinkUsername: string;
    initialLinkPassword: string;
    initialType: string;
    initialSupportWriting: boolean | null | undefined;
    initialCacheExpiresInSeconds: number | null;
    initialIgnoreCacheControlHeader: number | null;
    initialAuthType: string;
    typeOptions: Record<string, string>[];
    authTypes: Record<string, number>[];
    canEdit: boolean;
} & DialogProps;
interface Datasource {
    status: string;
    errors: string[];
    data: { keys: (string | null)[]; data: (string | null)[][] };
    count: number;
}
export default defineComponent({
    props: ["name", "testMode", "confirmCallback", "initialLinkUrl", "initialLinkHttpMethod", "initialLinkUsername", "initialAuthType", "initialLinkPassword", "initialType", "initialSupportWriting", "initialCacheExpiresInSeconds", "initialIgnoreCacheControlHeader", "typeOptions", "canEdit", "triggerElement" /*"initialSupportFiltering", "initialIsLegacyApi"*/],
    setup(props: Props, ctx: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const linkUrl = ref(props.initialLinkUrl as string);
        const linkHttpMethod = ref(props.initialLinkHttpMethod || "GET");
        const linkUsername = ref(props.initialLinkUsername as string);
        const linkPassword = ref(props.initialLinkPassword as string | null);
        const type = ref(props.initialType as string);
        const supportWriting = ref(props.initialSupportWriting ?? false);
        const authType = ref(props.initialAuthType as string | null);
        const cacheExpiresInSeconds = ref(props.initialCacheExpiresInSeconds as string | null);
        const ignoreCacheControlHeader = ref(props.initialIgnoreCacheControlHeader as boolean | null);
        const initialInfo = {
            status: null,
            errors: [],
            data: null,
            count: null,
        };
        const preview = ref(structuredClone(initialInfo) as unknown as Datasource);
        const confirm = ref(structuredClone(initialInfo) as unknown as Datasource);

        const mainStore = useMainStore();

        const showIgnoreCacheControlHeader = asyncComputed(() => mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.AllowIgnoreApiCacheControl.Id));
        const showSupportWriting = asyncComputed(async () => type.value == "Odata" && (supportWriting.value || mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.WritableODataDatasources.Id))); // TODO type constant

        const linkData = computed(() => {
            return {
                linkUrl: linkUrl.value,
                linkHttpMethod: linkHttpMethod.value,
                linkUsername: linkUsername.value,
                linkPassword: linkPassword.value,
                type: type.value,
                authType: authType.value,
                supportWriting: supportWriting.value,
                cacheExpiresInSeconds: cacheExpiresInSeconds.value,
                ignoreCacheControlHeader: ignoreCacheControlHeader.value,
            };
        });
        const linkHelp = computed(() => {
            return props.typeOptions.filter(i => i.value === linkData.value.type).map(i => i.help)[0];
        });

        const passwordLabel = computed(() => {
            return authType.value?.toString() === AuthType.Bearer.toString() ? "Bearer Token" : authType.value?.toString() === AuthType.APIKey.toString() ? "API Key" : authType.value?.toString() === AuthType.CustomHeader.toString() ? "Header Value" : "Password";
        });
        const usernameLabel = computed(() => {
            return authType.value?.toString() === AuthType.CustomHeader.toString() ? "Header" : "Username";
        });
        const filteredAuthTypes = computed(() => {
            if (linkData.value.type == "Socrata") {
                return Object.entries(AuthType)
                    .filter(([key]) => key.includes("Basic"))
                    .map(key => [{ label: transformKey(key[0]), value: key[1] }])
                    .flat();
            }
            return Object.entries(AuthType)
                .filter(([key]) => isNaN(parseInt(key)))
                .map(key => [{ label: transformKey(key[0]), value: key[1] }])
                .flat();
        });

        const linkDisabled = computed(() => {
            return (type.value != "Gov2GoServiceEvent" && authType.value == null) || !props.canEdit;
        });
        const passwordRequired = computed(() => {
            return authType.value?.toString() == AuthType.Basic.toString() || authType.value == AuthType.Bearer.toString() || authType.value == AuthType.APIKey.toString() || authType.value === AuthType.CustomHeader.toString();
        });
        const usernameRequired = computed(() => {
            return authType.value?.toString() == AuthType.Basic.toString() || authType.value === AuthType.CustomHeader.toString();
        });
        const basicAuthentication = computed(() => {
            return AuthType.Basic;
        });
        const noAuthentication = computed(() => {
            return AuthType.NoAuth;
        });
        const customHeader = computed(() => {
            return AuthType.CustomHeader;
        });
        watch(linkUsername, () => {
            if (props.initialLinkPassword != null && props.initialLinkPassword == linkPassword.value && linkUsername.value != props.initialLinkUsername) {
                linkPassword.value = null;
            }
        });
        watch(linkUrl, () => {
            if (props.initialLinkPassword != null && props.initialLinkPassword == linkPassword.value && linkUrl.value != props.initialLinkUrl) {
                linkPassword.value = null;
            }
        });
        watch(type, () => {
            if (props.initialAuthType == null && authType.value != null && type.value != props.initialType) {
                authType.value = null;
            }
            if (linkData.value.type == "Socrata") {
                authType.value = AuthType.Basic.toString();
            }
        });
        const transformKey = (key: string) => {
            let transformedKey = "";
            switch (key) {
                case "Basic":
                    transformedKey = "Basic Authentication";
                    break;
                case "NoAuth":
                    transformedKey = "No Authentication";
                    break;
                case "Bearer":
                    transformedKey = "Bearer Token";
                    break;
                case "APIKey":
                    transformedKey = "API Key";
                    break;
                case "CustomHeader":
                    transformedKey = "Custom Header";
                    break;
                default:
                    transformedKey = "Basic Authentication";
                    break;
            }
            return transformedKey;
        };
        const previewExternalLink = () => {
            if (!validation()) {
                return;
            }
            applyExternalLink("preview");
        };
        const close = () => {
            ctx.emit("close");
        };
        const validation = () => {
            const validation = confirm;
            const errors = [];

            if (!/^\s*\d*\s*$/.test(cacheExpiresInSeconds.value ?? "")) {
                errors.push("Expire must be a positive integer");
            } else if (/^\s*\d+\s*$/.test(cacheExpiresInSeconds.value ?? "")) {
                const cacheExpiresInSecondsNumber = parseInt(cacheExpiresInSeconds.value!);
                if (cacheExpiresInSecondsNumber < 5 || cacheExpiresInSecondsNumber > 86400) {
                    errors.push("Expire must be between 5 and 86400");
                }
            }

            if (type.value != "Gov2GoServiceEvent") {
                if (usernameRequired.value && linkUsername.value == null && passwordRequired && linkPassword.value == null) {
                    if (authType.value?.toString() == AuthType.Basic.toString()) {
                        errors.push("Username and Password are required");
                    } else if (authType.value?.toString() === AuthType.CustomHeader.toString()) {
                        errors.push("Header and Header Value are required");
                    }
                } else if (usernameRequired.value && linkUsername.value == null) {
                    if (authType.value?.toString() == AuthType.Basic.toString()) {
                        errors.push("Username is Required");
                    } else if (authType.value?.toString() === AuthType.CustomHeader.toString()) {
                        errors.push("Header is Required");
                    }
                } else if (passwordRequired.value && linkPassword.value == null) {
                    if (authType.value?.toString() == AuthType.Basic.toString()) {
                        errors.push("Password is Required");
                    } else if (authType.value?.toString() == AuthType.Bearer.toString()) {
                        errors.push("Bearer Token is Required");
                    } else if (authType.value?.toString() == AuthType.APIKey.toString()) {
                        errors.push("API Key is Required");
                    } else if (authType.value?.toString() == AuthType.CustomHeader.toString()) {
                        errors.push("Header Value is Required");
                    }
                }
            }
            validation.value.errors = errors;
            return errors.length == 0;
        };

        const confirmExternalLink = async () => {
            if (!props.canEdit) {
                return;
            }
            if (!validation()) {
                return;
            }
            await applyExternalLink("save");
            await props.confirmCallback({
                keys: confirm.value.data.keys,
                data: confirm.value.data.data,
                count: confirm.value.count,
                linkData: linkData,
            });
            close();
        };
        const applyExternalLink = async (subaction: string) => {
            if (!props.canEdit) {
                return;
            }
            const isPreview = subaction == "preview";
            const typeData = isPreview ? preview.value : confirm.value;
            typeData.status = "in-progress";
            typeData.errors = [];

            const uploadData = new FormData();
            uploadData.append("subAction", subaction);
            uploadData.append("name", props.name);
            uploadData.append("testMode", props.testMode.toString());
            uploadData.append("linkUrl", linkUrl.value);
            uploadData.append("linkHttpMethod", linkHttpMethod.value);
            uploadData.append("linkType", type.value);
            uploadData.append("linkPassword", linkPassword.value == undefined ? "" : linkPassword.value);
            uploadData.append("linkUsername", linkUsername.value == undefined ? "" : linkUsername.value);
            uploadData.append("authType", authType.value != null ? authType.value : "1");
            uploadData.append("cacheExpiresInSeconds", cacheExpiresInSeconds.value?.toString() ?? "");
            uploadData.append("supportWriting", supportWriting.value?.toString() ?? "");
            uploadData.append("ignoreCacheControlHeader", ignoreCacheControlHeader.value?.toString() ?? "");
            const uploadUrl = await mainStore.getUrl("adminDataSourceLinkExternal");
            try {
                const response = await fetch(uploadUrl, {
                    method: "POST",
                    headers: await getXsftHeader(mainStore),
                    credentials: "same-origin",
                    body: uploadData,
                });
                throwIfNonSuccessStatus(response);
                const responseJson = await response.json();
                if (responseJson.success) {
                    typeData.status = "ok";
                    //typeData.errors = [];
                    typeData.count = responseJson.rowCount;
                    typeData.data = {
                        keys: responseJson.keys,
                        data: responseJson.data,
                    };
                } else {
                    typeData.status = "fail";
                    typeData.errors = responseJson.errors;
                }
            } catch {
                typeData.status = "fail";
                typeData.errors = ["Unknown error occurred"];
            }
        };

        const setType = (typeI: typeof type.value) => {
            if (props.canEdit) {
                type.value = typeI;
            }
        };

        return {
            // Refs
            container: containerRef,

            // States
            type,
            linkUrl,
            linkHttpMethod,
            linkUsername,
            linkPassword,
            authType,
            supportWriting,
            cacheExpiresInSeconds,
            ignoreCacheControlHeader,
            preview,
            confirm,

            // Computed
            linkHelp,
            passwordLabel,
            usernameLabel,
            filteredAuthTypes,
            linkDisabled,
            passwordRequired,
            usernameRequired,
            basicAuthentication,
            noAuthentication,
            customHeader,
            transformKey,
            showIgnoreCacheControlHeader,
            showSupportWriting,
            // Methods
            previewExternalLink,
            close,
            confirmExternalLink,
            applyExternalLink,
            setType,
            validation,
            toggleIgnoreCacheControlHeader: () => {
                if (props.canEdit) {
                    ignoreCacheControlHeader.value = !ignoreCacheControlHeader.value;
                }
            },
            toggleSupportWriting: () => {
                if (props.canEdit) {
                    supportWriting.value = !supportWriting.value;
                }
            },
        };
    },
    components: {
        LoadingWheel,
        //HelpIcon,
    },
});
</script>
